// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, cloneElement, Children } from '../../utils';
import { safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Pagination from '../Pagination';
import Grid from '../Grid';
import GridCol from '../GridCol';
import Button from '../Button';
import Spinner from '../Spinner';
import Translate from '../Translate';
import CardListToolbar from './CardListToolbar';

import './CardList.less';

export const VIEW_TILE = 'tile';
export const VIEW_RESPONSIVE = 'responsive';
export const VIEW_EXTENDED = 'extended';
export const VIEW_ROW = 'row';

/**
 * `CardList` component.
 */
class CardList extends Component {
    constructor(props) {
        super(props);

        this.state = {
            page: props.defaultPage,
            totalItems: props.totalItems || props.items.length,
            pageSize: 10,
            items: props.items,
            view: props.view,
        };
    }

    componentDidMount() {
        this.fetchItems();
    }

    componentWillReceiveProps(nextProps) {
        const { view, items, totalItems } = nextProps;

        this.setState({
            items,
            totalItems: totalItems || items.length,
        });

        if (nextProps.view !== this.props.view && this.state.view !== VIEW_RESPONSIVE) {
            this.fetchItems({ page: 1, view });
        }
    }

    totalPages() {
        const { totalItems, pageSize } = this.state;
        return Math.ceil(totalItems / pageSize);
    }

    fetchItems({ page = this.state.page, view = this.state.view } = {}) {
        const { onItemsFetch } = this.props;
        const pageSize = view === VIEW_ROW ? 40 : 10;

        this.setState({
            view,
            page,
            pageSize,
        });
        safeInvoke(onItemsFetch, { page, pageSize });
    }

    loadMore() {
        const { onItemsFetch } = this.props;
        const { page } = this.state;
        const nextPage = page + 1;

        this.setState({
            page: nextPage,
            pageSize: 10,
        });
        safeInvoke(onItemsFetch, { page: nextPage, pageSize: 10 });
    }

    isTile() {
        const { view } = this.state;

        return view === VIEW_TILE || view === VIEW_RESPONSIVE;
    }

    renderPagination() {
        const { baseClassName } = this.props;
        const { page, view } = this.state;
        const totalPages = this.totalPages();

        if (totalPages < 2) {
            return null;
        }

        if (view === VIEW_RESPONSIVE && page < totalPages) {
            return (
                <div className={`${baseClassName}__pagination`}>
                    <Button onClick={() => this.loadMore()}>
                        <Translate content={'CardList.loadMore'} fallback={'Load more'} />
                    </Button>
                </div>
            );
        }

        return (
            <Pagination
                className={`${baseClassName}__pagination`}
                total={totalPages}
                current={page}
                onSelect={page => this.fetchItems({ page })}
            />
        );
    }

    changeView(view) {
        const { onViewChange } = this.props;

        this.fetchItems({ page: 1, view });
        safeInvoke(onViewChange, view);
    }

    select(keys) {
        const { onSelectionChange } = this.props;
        onSelectionChange(keys);
    }

    selectAll = () => {
        const { items } = this.state;
        this.select(items.map(this.getItemKey));
    };

    selectNone = () => {
        this.select([]);
    };

    getItemKey = item => {
        const { itemKey } = this.props;
        return item[itemKey];
    };

    itemSelection(item) {
        const key = this.getItemKey(item);
        const { selection } = this.props;

        return {
            isSelected: selection.indexOf(key) > -1,
            onSelectionChange: ({ isSelected }) => {
                const keys = isSelected
                    ? [...selection, key]
                    : selection.filter(k => k !== key);
                this.select(keys);
            },
        };
    }

    renderItem = item => {
        const { baseClassName, itemRender, onSelectionChange } = this.props;
        const { view } = this.state;

        return cloneElement(itemRender(item), {
            key: this.getItemKey(item),
            view: this.isTile() ? VIEW_TILE : view,
            className: classNames(`${baseClassName}__list-item`),
            selection: onSelectionChange ? this.itemSelection(item) : null,
        });
    };

    previousView = null;

    handleResize = ({ entry: { width } }) => {
        const view = width > 584 ? this.previousView : VIEW_RESPONSIVE;

        if (view === VIEW_RESPONSIVE && !this.previousView) {
            this.previousView = this.state.view;
        } else if (view !== VIEW_RESPONSIVE) {
            this.previousView = null;
        }

        if (view && view !== this.state.view) {
            this.changeView(view);
        }
    };

    renderLoader() {
        const { baseClassName, loading } = this.props;

        if (!loading) {
            return null;
        }

        return (
            <div className={`${baseClassName}__content-blocker`}>
                <div className={`${baseClassName}__loader`}>
                    <Spinner className={`${baseClassName}__loader-spinner`} />
                </div>
            </div>
        );
    }

    render() {
        const {
            baseClassName,
            className,
            items,
            onItemsFetch,
            view,
            itemKey,
            itemRender,
            onViewChange,
            selection,
            onSelectionChange,
            selectionButton,
            viewSwitcher,
            toolbarGroups,
            totalItems,
            defaultPage,
            loading,
            ...props
        } = this.props;
        let content = this.state.items.map(this.renderItem);

        if (this.isTile()) {
            content = (
                <Grid minColumnWidth={400} gap="md">
                    {Children.map(content, child => (
                        <GridCol key={child.props.key}>
                            {child}
                        </GridCol>
                    ))}
                </Grid>
            );
        }

        return (
            <Measure onResize={this.handleResize}>
                {({ measureRef }) => (
                    <div
                        ref={measureRef}
                        className={classNames(baseClassName, className)}
                        {...props}
                    >
                        <CardListToolbar
                            selection={onSelectionChange && this.state.totalItems > 1
                                ? {
                                    ...selectionButton,
                                    total: this.state.totalItems,
                                    onSelectAll: this.selectAll,
                                    onSelectNone: this.selectNone,
                                    checked: this.state.items.length === selection.length,
                                }
                                : null
                            }
                            onViewChange={viewSwitcher ? view => this.changeView(view) : null}
                            currentView={this.state.view}
                            groups={toolbarGroups}
                        />
                        {this.state.items.length > 0 && (
                            <div className={`${baseClassName}__list ${baseClassName}__list--${this.state.view}`}>
                                {content}
                            </div>
                        )}
                        {this.state.items.length === 0 && !loading && (
                            <Translate content="CardList.notFound" fallback="No items found." />
                        )}
                        {this.renderPagination()}
                        {this.renderLoader()}
                    </div>
                )}
            </Measure>
        );
    }
}

CardList.propTypes = {
    /**
     * Array of data items for rendering.
     */
    items: PropTypes.array.isRequired,
    /**
     * Callback for fetching items.
     */
    onItemsFetch: PropTypes.func,
    /**
     * Total items. Equal items.length by default.
     */
    totalItems: PropTypes.number,
    /**
     * Default page number.
     */
    defaultPage: PropTypes.number,
    /**
     * View mode.
     */
    view: PropTypes.oneOf([VIEW_EXTENDED, VIEW_TILE, VIEW_ROW, VIEW_RESPONSIVE]),
    /**
     * View change callback.
     */
    onViewChange: PropTypes.func,
    /**
     * View switcher config.
     */
    viewSwitcher: PropTypes.bool,
    /**
     * Array of keys of selected cards.
     */
    selection: PropTypes.array,
    /**
     * Selection change handler for list. Checkboxes will only be rendered if this prop is defined.
     */
    onSelectionChange: PropTypes.func,
    /**
     * Config for selection button in the toolbar.
     */
    selectionButton: PropTypes.shape({
        /**
         * See [Badge](#!/Badge) for all props.
         */
        badge: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object,
        ]),
        /**
         * Array of additional [MenuItems](#!/MenuItem).
         */
        menuItems: PropTypes.arrayOf(
            PropTypes.element,
        ),
    }),
    /**
     * Toolbar groups for the toolbar.
     */
    toolbarGroups: PropTypes.arrayOf(
        PropTypes.element,
    ),
    /**
     * Unique key.
     */
    itemKey: PropTypes.string.isRequired,
    /**
     * Render function for item.
     */
    itemRender: PropTypes.func.isRequired,
    /**
     * Indicate the loading state of the list.
     */
    loading: PropTypes.bool,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

CardList.defaultProps = {
    onItemsFetch: null,
    totalItems: null,
    defaultPage: 1,
    view: VIEW_EXTENDED,
    onViewChange: null,
    viewSwitcher: false,
    selection: [],
    onSelectionChange: null,
    selectionButton: {
        badge: null,
        menuItems: [],
    },
    toolbarGroups: [],
    loading: false,
    className: null,
    baseClassName: `${CLS_PREFIX}card-list`,
};

export default CardList;



// WEBPACK FOOTER //
// ./components/CardList/CardList.js